import React from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";

const CURRENT_PRODUCT = "__current__";

function id(value) {
  return String(value?.id || value?._id || value || "").trim();
}
function name(value) {
  return String(value?.name || value?.nombre || "Producto").trim();
}
function variantsFor(product) {
  return (Array.isArray(product?.variants) ? product.variants : [])
    .filter((variant) => variant?.active !== false)
    .map((variant) => ({ id: id(variant), label: variant.label || variant.sku || id(variant) }))
    .filter((variant) => variant.id);
}
function newDraft(kind, currentProductId) {
  const pack = kind === "same_product_pack";
  return {
    key: `promo-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    id: "",
    name: pack ? "Pack especial" : "Combo",
    active: true,
    type: kind,
    fixedTotal: "",
    reviewRequired: false,
    reviewReasons: [],
    lines: pack
      ? [{ productId: currentProductId || CURRENT_PRODUCT, quantity: "2", variantMode: "any", variantIds: [] }]
      : [
          { productId: currentProductId || CURRENT_PRODUCT, quantity: "1", variantMode: "any", variantIds: [] },
          { productId: "", quantity: "1", variantMode: "any", variantIds: [] },
        ],
  };
}

function PromotionLine({ line, index, products, currentProductId, currentProduct, pack, onChange, onRemove }) {
  const resolvedProductId = line.productId === CURRENT_PRODUCT ? currentProductId : line.productId;
  const product = resolvedProductId
    ? products.find((item) => id(item) === resolvedProductId) || (currentProductId === resolvedProductId ? currentProduct : null)
    : line.productId === CURRENT_PRODUCT ? currentProduct : null;
  const variantOptions = variantsFor(product);
  const selectedVariants = variantOptions.filter((variant) => (line.variantIds || []).includes(variant.id));

  return (
    <Paper variant="outlined" sx={{ p: 1.4, borderRadius: 2.5 }}>
      <Stack spacing={1.2}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "flex-start" }}>
          <TextField
            select
            label={pack ? "Producto" : `Producto ${index + 1}`}
            value={line.productId}
            onChange={(event) => onChange({ ...line, productId: event.target.value, variantMode: "any", variantIds: [] })}
            disabled={pack}
            fullWidth
            helperText={pack ? "Este pack corresponde al producto que estás editando." : "Elegí un producto real de tu Carta."}
          >
            {line.productId === CURRENT_PRODUCT ? <MenuItem value={CURRENT_PRODUCT}>Este producto</MenuItem> : null}
            {products.map((item) => <MenuItem key={id(item)} value={id(item)}>{name(item)}</MenuItem>)}
          </TextField>
          <TextField
            label="Cantidad"
            type="number"
            value={line.quantity}
            onChange={(event) => onChange({ ...line, quantity: event.target.value })}
            inputProps={{ min: pack ? 2 : 1, max: 99, step: 1 }}
            sx={{ width: { xs: "100%", sm: 150 } }}
          />
          {!pack ? (
            <Button type="button" color="error" startIcon={<DeleteRoundedIcon />} onClick={onRemove} sx={{ textTransform: "none", minHeight: 56 }}>
              Quitar
            </Button>
          ) : null}
        </Stack>
        {variantOptions.length ? (
          <Stack spacing={1}>
            <TextField
              select
              label="Opciones que participan"
              value={line.variantMode === "specific" ? "specific" : "any"}
              onChange={(event) => onChange({ ...line, variantMode: event.target.value, variantIds: event.target.value === "specific" ? line.variantIds || [] : [] })}
              helperText="‘Cualquier opción’ incluye también variantes de mayor precio; el servidor sólo aplica la promo si realmente genera ahorro."
            >
              <MenuItem value="any">Cualquier opción</MenuItem>
              <MenuItem value="specific">Sólo opciones elegidas</MenuItem>
            </TextField>
            {line.variantMode === "specific" ? (
              <Autocomplete
                multiple
                options={variantOptions}
                value={selectedVariants}
                onChange={(_event, values) => onChange({ ...line, variantIds: values.map((value) => value.id) })}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => <TextField {...params} label="Variantes habilitadas" helperText="Las variantes inactivas no califican al momento de comprar." />}
              />
            ) : null}
          </Stack>
        ) : null}
      </Stack>
    </Paper>
  );
}

function PromotionCard({ draft, index, products, currentProductId, currentProduct, canManage, onChange, onRemove }) {
  const pack = draft.type === "same_product_pack";
  const updateLine = (lineIndex, nextLine) => onChange({
    ...draft,
    lines: draft.lines.map((line, currentIndex) => currentIndex === lineIndex ? nextLine : line),
  });
  return (
    <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 3, bgcolor: "#fff" }}>
      <Stack spacing={1.5}>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={1}>
          <Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
              <Typography sx={{ fontWeight: 950, color: "#19242F" }}>{pack ? "Pack del mismo producto" : "Combo de productos"}</Typography>
              {draft.id ? <Chip size="small" label={`Guardada · rev. ${draft.revision || 1}`} /> : <Chip size="small" color="info" label="Se guardará al crear el producto" />}
              {draft.reviewRequired ? <Chip size="small" color="warning" label="Revisar" /> : null}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
              {pack ? "Ej.: 2 hamburguesas por $15.000. Las unidades sobrantes conservan su precio normal." : "Ej.: hamburguesa + papas + bebida por un precio final."}
            </Typography>
          </Box>
          <FormControlLabel
            control={<Switch checked={draft.active !== false} onChange={(event) => onChange({ ...draft, active: event.target.checked })} />}
            label="Activa"
            disabled={!canManage}
          />
        </Stack>
        {draft.reviewReasons?.length ? <Alert severity="warning">{draft.reviewReasons.join(" ")}</Alert> : null}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <TextField label="Nombre comercial" value={draft.name} onChange={(event) => onChange({ ...draft, name: event.target.value })} fullWidth disabled={!canManage} />
          <TextField label="Precio final" type="number" value={draft.fixedTotal} onChange={(event) => onChange({ ...draft, fixedTotal: event.target.value })} inputProps={{ min: 0.01, step: "0.01" }} fullWidth disabled={!canManage} helperText="Precio del pack/combo completo, no precio unitario." />
        </Stack>
        <Divider />
        <Stack spacing={1}>
          {(draft.lines || []).map((line, lineIndex) => (
            <PromotionLine
              key={`${draft.key || draft.id}-${lineIndex}`}
              line={line}
              index={lineIndex}
              products={products}
              currentProductId={currentProductId}
              currentProduct={currentProduct}
              pack={pack}
              onChange={(nextLine) => updateLine(lineIndex, nextLine)}
              onRemove={() => onChange({ ...draft, lines: draft.lines.filter((_, currentIndex) => currentIndex !== lineIndex) })}
            />
          ))}
          {!pack ? (
            <Button
              type="button"
              variant="outlined"
              startIcon={<AddRoundedIcon />}
              onClick={() => onChange({ ...draft, lines: [...draft.lines, { productId: "", quantity: "1", variantMode: "any", variantIds: [] }] })}
              disabled={!canManage || draft.lines.length >= 12}
              sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 850 }}
            >
              Agregar producto al combo
            </Button>
          ) : null}
        </Stack>
        <Button type="button" color="error" onClick={onRemove} disabled={!canManage} sx={{ alignSelf: "flex-start", textTransform: "none" }}>
          {draft.id ? "Eliminar promoción" : "Descartar promoción"}
        </Button>
      </Stack>
    </Paper>
  );
}

export default function GastronomicPromotionsEditor({
  drafts = [],
  setDrafts,
  products = [],
  currentProductId = "",
  currentProduct = null,
  canManage = true,
  loading = false,
  error = "",
}) {
  const add = (type) => setDrafts((current) => [...current, newDraft(type, currentProductId)]);
  const visibleDrafts = drafts.filter((draft) => !draft._delete);
  const replaceDraft = (target, next) => setDrafts((current) => current.map((item) => (item.key || item.id) === (target.key || target.id) ? next : item));
  const removeDraft = (target) => setDrafts((current) => current
    .map((item) => (item.key || item.id) === (target.key || target.id) ? { ...item, _delete: true } : item)
    .filter((item) => !(item._delete && !item.id)));
  return (
    <Stack spacing={1.5}>
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 950, color: "#19242F" }}>Promociones y combos</Typography>
        <Typography variant="body2" color="text.secondary">
          Los packs y combos son reglas comerciales reales. El checkout vuelve a calcularlas en el servidor y nunca confía en un total enviado por el navegador.
        </Typography>
      </Box>
      {!canManage ? <Alert severity="info">Podés ver las promociones, pero tu rol necesita permiso de edición de Ítems para modificarlas.</Alert> : null}
      {error ? <Alert severity="error">{error}</Alert> : null}
      {loading ? <Alert severity="info">Cargando promociones…</Alert> : null}
      <Stack spacing={1.25}>
        {visibleDrafts.map((draft, index) => (
          <PromotionCard
            key={draft.key || draft.id || index}
            draft={draft}
            index={index}
            products={products}
            currentProductId={currentProductId}
            currentProduct={currentProduct}
            canManage={canManage}
            onChange={(next) => replaceDraft(draft, next)}
            onRemove={() => removeDraft(draft)}
          />
        ))}
      </Stack>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
        <Button type="button" variant="outlined" startIcon={<AddRoundedIcon />} onClick={() => add("same_product_pack")} disabled={!canManage} sx={{ textTransform: "none", fontWeight: 900 }}>
          Crear “2 por $X” / pack
        </Button>
        <Button type="button" variant="outlined" startIcon={<AddRoundedIcon />} onClick={() => add("multi_product_combo")} disabled={!canManage} sx={{ textTransform: "none", fontWeight: 900 }}>
          Crear combo con otros productos
        </Button>
      </Stack>
      <Alert severity="info">
        “Más unidades, mejor precio” es distinto: allí definís el precio <strong>por unidad</strong> desde cierta cantidad. Un pack “2 por $X” tiene un precio total exacto.
      </Alert>
    </Stack>
  );
}

export { CURRENT_PRODUCT };
